import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { OperationObserverService } from '@x/common/operation';
import { TEWishlistAddItem } from '@x/common/tracking/event-models/wishlist.events';
import { TrackingService } from '@x/common/tracking/tracking.service';
import { ChannelContextService } from '@x/ecommerce-shop/app/channel/services/channel-context.service';
import { AuthLogout, AuthSetUser } from '@x/ecommerce-shop/app/core/auth/state/auth.actions';
import {
  WishlistAddActionToQueue,
  WishlistAddToProductIds,
  WishlistClear,
  WishlistRemoveFromProductIds,
  WishlistTempItem,
} from '@x/ecommerce-shop/app/core/whishlist/state/wishlist.actions';
import { ShopWishlistService } from '@x/ecommerce/shop-client';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

export interface WishlistStateModel {
  wishlistTempItem: number | null;
  wishlistProductIds: number[];
}

const defaults: WishlistStateModel = {
  wishlistTempItem: null,
  wishlistProductIds: [],
};

@State<WishlistStateModel>({
  name: 'wishlistState',
  defaults,
})
@Injectable()
export class WishlistState implements NgxsOnInit {
  _q = this.operationService.createQueue();

  constructor(
    private channelContext: ChannelContextService,
    private shopWishlistService: ShopWishlistService,
    private operationService: OperationObserverService,
    private trackingService: TrackingService,
  ) {}

  @Selector()
  static wishlistProductIds(state: WishlistStateModel): number[] {
    return state.wishlistProductIds;
  }

  @Action(AuthSetUser, { cancelUncompleted: true })
  getWishlistProductIds(state: StateContext<WishlistStateModel>) {
    const channelCode = this.channelContext.channelCode;

    if (!channelCode) return;

    const ids$ = this.shopWishlistService
      .getWishListItemIds({
        channelCode,
        page: {
          index: 0,
          size: 9999,
        },
      })
      .pipe(catchError(() => []));

    return ids$.pipe(
      tap((wishlistItemProductIds) => {
        state.patchState({ wishlistProductIds: wishlistItemProductIds });
      }),
      switchMap(() => {
        const { wishlistTempItem } = state.getState();

        if (!wishlistTempItem) return of('complete');

        return state.dispatch(new WishlistAddToProductIds(wishlistTempItem));
      }),
    );
  }

  @Action(WishlistAddToProductIds)
  updateProductIds(
    state: StateContext<WishlistStateModel>,
    { productId, context }: WishlistAddToProductIds,
  ) {
    const { wishlistProductIds } = state.getState();

    if (wishlistProductIds.includes(productId)) return;

    return this.shopWishlistService.addWishlistItem({ productId }).pipe(
      tap((newId) => {
        state.patchState({ wishlistProductIds: [...wishlistProductIds, newId] });

        this.trackingService.sendEvent(
          new TEWishlistAddItem(this.trackingService.routeOrigin, { productId: newId, context }),
        );
      }),
    );
  }

  @Action(WishlistRemoveFromProductIds)
  removeFromProductIds(
    state: StateContext<WishlistStateModel>,
    { productId }: WishlistRemoveFromProductIds,
  ) {
    const { wishlistProductIds } = state.getState();

    if (!wishlistProductIds.includes(productId)) return;

    return this.shopWishlistService.removeWishlistItem({ productId }).pipe(
      tap((removeId) => {
        const newProductIds = [...wishlistProductIds.filter((id) => id !== removeId)];
        state.patchState({ wishlistProductIds: newProductIds });
      }),
    );
  }

  @Action(WishlistAddActionToQueue)
  wishlistAddActionToQueue(
    state: StateContext<WishlistStateModel>,
    { action }: WishlistAddActionToQueue,
  ) {
    return this._q.queue(of('add_to_q').pipe(switchMap(() => state.dispatch(action))));
  }

  @Action(WishlistTempItem)
  wishlistTempItem(state: StateContext<WishlistStateModel>, { productId }: WishlistTempItem) {
    state.patchState({ wishlistTempItem: productId });
  }

  @Action(AuthLogout)
  @Action(WishlistClear)
  clear(state: StateContext<WishlistStateModel>) {
    state.patchState({ ...defaults });
  }

  ngxsOnInit(): void {
    this._q.observe().subscribe();
  }
}
